
import React, { useState } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, TextField, MenuItem, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, LinearProgress
} from '@mui/material';
import {
    Download as DownloadIcon, Print as PrintIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';

const StudentSummary = () => {
    const [classFilter, setClassFilter] = useState('');

    const classData = [
        { class: 'Nursery', sections: ['A'], boys: 15, girls: 18, total: 33, rte: 4, sc: 3, st: 1, obc: 8 },
        { class: 'LKG', sections: ['A', 'B'], boys: 28, girls: 25, total: 53, rte: 6, sc: 5, st: 2, obc: 12 },
        { class: 'UKG', sections: ['A', 'B'], boys: 30, girls: 27, total: 57, rte: 5, sc: 4, st: 1, obc: 14 },
        { class: 'Class 1', sections: ['A', 'B'], boys: 32, girls: 30, total: 62, rte: 7, sc: 6, st: 2, obc: 15 },
        { class: 'Class 2', sections: ['A', 'B'], boys: 28, girls: 31, total: 59, rte: 5, sc: 5, st: 3, obc: 13 },
        { class: 'Class 3', sections: ['A', 'B'], boys: 35, girls: 29, total: 64, rte: 6, sc: 7, st: 2, obc: 16 },
        { class: 'Class 4', sections: ['A'], boys: 22, girls: 20, total: 42, rte: 4, sc: 3, st: 1, obc: 10 },
        { class: 'Class 5', sections: ['A', 'B'], boys: 33, girls: 28, total: 61, rte: 5, sc: 6, st: 2, obc: 14 },
        { class: 'Class 6', sections: ['A', 'B'], boys: 30, girls: 32, total: 62, rte: 7, sc: 5, st: 3, obc: 15 },
        { class: 'Class 7', sections: ['A'], boys: 21, girls: 19, total: 40, rte: 3, sc: 4, st: 1, obc: 9 },
        { class: 'Class 8', sections: ['A', 'B'], boys: 38, girls: 34, total: 72, rte: 8, sc: 7, st: 2, obc: 18 },
        { class: 'Class 9', sections: ['A', 'B'], boys: 36, girls: 30, total: 66, rte: 6, sc: 6, st: 3, obc: 16 },
        { class: 'Class 10', sections: ['A', 'B'], boys: 40, girls: 35, total: 75, rte: 7, sc: 8, st: 2, obc: 19 },
    ];

    const totalBoys = classData.reduce((a, c) => a + c.boys, 0);
    const totalGirls = classData.reduce((a, c) => a + c.girls, 0);
    const grandTotal = classData.reduce((a, c) => a + c.total, 0);
    const totalRTE = classData.reduce((a, c) => a + c.rte, 0);

    const filteredData = classData.filter(c => !classFilter || c.class === classFilter);

    const genderChart = {
        labels: ['Boys', 'Girls'],
        datasets: [{ data: [totalBoys, totalGirls], backgroundColor: ['#2196f3', '#e91e63'], borderWidth: 0 }],
    };

    const categoryChart = {
        labels: ['SC', 'ST', 'OBC', 'General'],
        datasets: [{
            data: [
                classData.reduce((a, c) => a + c.sc, 0),
                classData.reduce((a, c) => a + c.st, 0),
                classData.reduce((a, c) => a + c.obc, 0),
                grandTotal - classData.reduce((a, c) => a + c.sc + c.st + c.obc, 0),
            ],
            backgroundColor: ['#ff9800', '#4caf50', '#2196f3', '#9c27b0'],
            borderWidth: 0,
        }],
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Student Summary</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<PrintIcon />} size="small">Print</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Excel Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1a9e8f, #26c6b8)' }}>
                        <CardContent sx={{ textAlign: 'center', color: '#fff' }}>
                            <PeopleIcon sx={{ fontSize: 28, mb: 0.5 }} />
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Students</Typography>
                            <Typography variant="h3" fontWeight={700}>{grandTotal}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Boys</Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: '#2196f3' }}>{totalBoys}</Typography>
                            <Typography variant="caption" color="text.secondary">{Math.round((totalBoys / grandTotal) * 100)}%</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #e91e63' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Girls</Typography>
                            <Typography variant="h4" fontWeight={700} sx={{ color: '#e91e63' }}>{totalGirls}</Typography>
                            <Typography variant="caption" color="text.secondary">{Math.round((totalGirls / grandTotal) * 100)}%</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">RTE Students</Typography>
                            <Typography variant="h4" fontWeight={700}>{totalRTE}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Gender Distribution</Typography>
                            <Box sx={{ maxWidth: 200, mx: 'auto' }}>
                                <Doughnut data={genderChart} options={{ plugins: { legend: { position: 'bottom' } }, cutout: '65%' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Category Distribution</Typography>
                            <Box sx={{ maxWidth: 200, mx: 'auto' }}>
                                <Doughnut data={categoryChart} options={{ plugins: { legend: { position: 'bottom' } }, cutout: '65%' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700}>Class-wise Strength</Typography>
                        <TextField fullWidth select size="small" label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)} sx={{ maxWidth: 200 }}>
                            <MenuItem value="">All Classes</MenuItem>
                            {classData.map(c => <MenuItem key={c.class} value={c.class}>{c.class}</MenuItem>)}
                        </TextField>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Sections</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#2196f3' }}>Boys</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#e91e63' }}>Girls</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>RTE</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>SC</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>ST</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>OBC</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Strength</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map(c => (
                                    <TableRow key={c.class} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{c.class}</TableCell>
                                        <TableCell>{c.sections.join(', ')}</TableCell>
                                        <TableCell align="center" sx={{ color: '#2196f3', fontWeight: 600 }}>{c.boys}</TableCell>
                                        <TableCell align="center" sx={{ color: '#e91e63', fontWeight: 600 }}>{c.girls}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>{c.total}</TableCell>
                                        <TableCell align="center">{c.rte}</TableCell>
                                        <TableCell align="center">{c.sc}</TableCell>
                                        <TableCell align="center">{c.st}</TableCell>
                                        <TableCell align="center">{c.obc}</TableCell>
                                        <TableCell sx={{ minWidth: 100 }}>
                                            <LinearProgress variant="determinate" value={Math.min((c.total / 80) * 100, 100)}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: c.total > 60 ? '#e53935' : c.total > 40 ? '#ff9800' : '#4caf50' } }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell>{classData.reduce((a, c) => a + c.sections.length, 0)} sections</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#2196f3' }}>{totalBoys}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#e91e63' }}>{totalGirls}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{grandTotal}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{totalRTE}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{classData.reduce((a, c) => a + c.sc, 0)}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{classData.reduce((a, c) => a + c.st, 0)}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{classData.reduce((a, c) => a + c.obc, 0)}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default StudentSummary;
